<template>
  <div v-if="!isEmptyData || pageLoading" class="outbound-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    />
    <div class="outbound-report-content">
      <OutboundReportApplicationsWidget
        class="outbound-applications"
        :data="flatListOfSources"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <OutboundReportHiresWidget
        class="outbound-hires"
        :data="flatListOfSources"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <OutboundReportSummary
        class="summary mb-0"
        :loading="pageLoading"
        :data="categoryTotals"
        :tooltips="tooltips"
        :project-name="projectName"
        :date-range="dateRange"
      />
      <OutboundReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :prospecting-rows="prospectingRows"
        :agency-rows="agencyRows"
        :table-totals="tableTotals"
        :downloading="downloading"
        @download-csv="downloadOutboundTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, ref,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useOutboundReport } from '@/stores/insights/outboundReport';
import OutboundReportApplicationsWidget from '@/components/insights/SourceInsights/OutboundReport/OutboundReportApplicationsWidget.vue';
import OutboundReportTable from '@/components/insights/SourceInsights/OutboundReport/OutboundReportTable.vue';
import { useInsights } from '@/stores/insights/insights';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import OutboundReportHiresWidget from '@/components/insights/SourceInsights/OutboundReport/OutboundReportHiresWidget.vue';
import OutboundReportSummary from '@/components/insights/SourceInsights/OutboundReport/OutboundReportSummary.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useTooltips } from '@/stores/insights/tooltips';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  isEmptyData,
  data,
  prospectingRows,
  agencyRows,
  error,
  tableTotals,
  flatListOfSources,
  categoryTotals,
} = storeToRefs(useOutboundReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { reportingMessage } = storeToRefs(useInsights());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadOutboundReport, downloadOutboundCSV } = useOutboundReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const downloading = ref(false);

const projectName = computed(() => authData.value.projectName);

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadOutboundReport(params),
]);

const downloadOutboundTable = async () => {
  downloading.value = true;

  await downloadOutboundCSV({
    dateRange: dateRange.value,
    projectName: projectName.value,
  });

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Data Table (.csv)',
  });

  downloading.value = false;
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Outbound Report' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'source',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: {
      'outbound-top-sources-by-application': 'Graph: Source Distribution of Apps',
      'outbound-top-sources-by-hire': 'Graph: Source Distribution of Hires',
      prospecting_applications: 'Metric: % Apps from Prospecting',
      prospecting_hired: 'Metric: % Hires from Prospecting',
      agencies_applications: 'Metric: % Apps from Agencies',
      agencies_hired: 'Metric: % Hires from Agencies',
      'outbound-report-table': 'Table: Outbound Source Data',
    },
    csvOptions: [
      {
        label: 'Data Table (.csv)',
        csvDownloadFunction: () => downloadOutboundCSV({
          dateRange: dateRange.value,
          projectName: projectName.value,
        }),
      },
    ],
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});
</script>

<style lang="less" scoped>
.outbound-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.outbound-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "outboundApplications outboundHires"
    "summary summary"
    "table table";
  grid-template-rows: 1fr auto auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.summary {
  grid-area: summary;
}

.outbound-applications {
  grid-area: outboundApplications;
}

.outbound-hires {
  grid-area: outboundHires;
}

.table {
  grid-area: table;
}
</style>
